// Common form utility functions

// Note: columnOptions and operatorOptions are now dynamic and come from metadataFields
// These are kept for backward compatibility but should be phased out

export const modelOptions = {
  classification: [
    { value: 'LogisticRegression', label: 'Logistic Regression' },
    { value: 'DecisionTreeClassifier', label: 'Decision Tree Classifier' },
    { value: 'GradientBoostingClassifier', label: 'Gradient Boosting Classifier' },
    { value: 'ExtraTreesClassifier', label: 'Extra Trees Classifier' },
    { value: 'RandomForestClassifier', label: 'Random Forest Classifier' }
  ],
  regression: [
    { value: 'LinearRegression', label: 'Linear Regression' },
    { value: 'Ridge', label: 'Ridge Regression' },
    { value: 'Lasso', label: 'Lasso Regression' },
    { value: 'ElasticNet', label: 'Elastic Net' },
    { value: 'Lars', label: 'LARS' },
    { value: 'LassoLars', label: 'Lasso LARS' },
    { value: 'BayesianRidge', label: 'Bayesian Ridge' },
    { value: 'PassiveAggressiveRegressor', label: 'Passive Aggressive Regressor' },
    { value: 'HuberRegressor', label: 'Huber Regressor' },
    { value: 'KMeans', label: 'K-Means' },
    { value: 'PLSRegression', label: 'PLS Regression' },
    { value: 'DecisionTreeRegressor', label: 'Decision Tree Regressor' },
    { value: 'GradientBoostingRegressor', label: 'Gradient Boosting Regressor' },
    { value: 'ExtraTreesRegressor', label: 'Extra Trees Regressor' },
    { value: 'RandomForestRegressor', label: 'Random Forest Regressor' }
  ]
};

export const scalerOptions = [
  { value: 'RobustScaler', label: 'Robust Scaler' },
  { value: 'StandardScaler', label: 'Standard Scaler' },
  { value: 'MinMaxScaler', label: 'Min-Max Scaler' },
  { value: 'MaxAbsScaler', label: 'Max Absolute Scaler' },
  { value: 'None', label: 'None' }
];

export const getMetricsOptions = (modelingType) => {
  if (modelingType.name === 'classification') {
    if (modelingType.type === 'binary') {
      return [
        { value: 'Accuracy', label: 'Accuracy' },
        { value: 'Precision', label: 'Precision' },
        { value: 'Recall(Sensitivity)', label: 'Recall(Sensitivity)' },
        { value: 'F1', label: 'F1' },
        { value: 'AUC', label: 'AUC' },
        { value: 'Specificity', label: 'Specificity' },
        { value: 'Sensitivity', label: 'Sensitivity' }
      ];
    } else {
      return [
        { value: 'Accuracy', label: 'Accuracy' },
        { value: 'Precision', label: 'Precision' },
        { value: 'Recall(Sensitivity)', label: 'Recall(Sensitivity)' },
        { value: 'F1', label: 'F1' }
      ];
    }
  } else {
    return [
      { value: 'MAE', label: 'MAE' },
      { value: 'MSE', label: 'MSE' },
      { value: 'RMSE', label: 'RMSE' },
      { value: 'R2', label: 'R2' }
    ];
  }
};

export const defaultFormData = {
  // General
  name: '',
  description: '',

  // Data Source
  selected_feature_extraction_job: null,
  data_source: null,

  // Data Processing
  filter_data: [
    { column_name: '', operator: '=', value: '' }
  ],
  fill_na: 0,

  // Training
  attribute: 'Cancer Status',
  filter_data_training: [
    { column_name: '', operator: '=', value: '' }
  ],
  features_to_include: {
    include_vocs: true,
    include_demo: false,
    include_mirex: false
  },
  validation_technique: {
    name: 'k-fold',
    k: 5,
    test_size: 0.2
  },

  // Modeling
  modeling_type: {
    name: 'classification',
    type: 'binary',
    case: ['Bladder Cancer Positive'],
    control: ['Healthy'],
    classes: []
  },
  model: 'LogisticRegression',
  normalize: 'RobustScaler',

  // Evaluation
  evaluation_metrics: {
    evaluation_metrics_list: ['Accuracy', 'Precision', 'Recall(Sensitivity)'],
    top_n: 1
  }
};

export const buildQuery = (formData) => {
  const query = {
    // Add data_source if selected
    ...(formData.data_source && { data_source: formData.data_source }),

    filter_data: formData.filter_data.reduce((acc, filter) => {
      if (filter.value && filter.column_name && filter.operator) {
        acc[filter.column_name] = {
          operator: filter.operator,
          value: filter.operator === 'in' || filter.operator === 'not in'
            ? filter.value.split(',').map(v => v.trim())
            : filter.value
        };
      }
      return acc;
    }, {}),
    fill_na: formData.fill_na,
    attribute: formData.attribute,
    filter_data_training: formData.filter_data_training.reduce((acc, filter) => {
      if (filter.value && filter.column_name && filter.operator) {
        acc[filter.column_name] = {
          operator: filter.operator,
          value: filter.operator === 'in' || filter.operator === 'not in'
            ? filter.value.split(',').map(v => v.trim())
            : filter.value
        };
      }
      return acc;
    }, {}),
    features_to_include: {
      include_vocs: formData.features_to_include.include_vocs ? 1 : 0,
      include_demo: formData.features_to_include.include_demo ? 1 : 0,
      include_mirex: formData.features_to_include.include_mirex ? 1 : 0
    },
    validation_technique: formData.validation_technique.name === 'k-fold'
      ? { name: 'k-fold', k: formData.validation_technique.k }
      : { name: 'train-test-split', test_size: formData.validation_technique.test_size },
    modeling_type: formData.modeling_type,
    model: formData.model,
    normalize: formData.normalize === 'None' ? null : formData.normalize,
    evaluation_metrics: {
      evaluation_metrics_list: formData.evaluation_metrics?.evaluation_metrics_list || [],
      top_n: formData.evaluation_metrics?.top_n || 1
    }
  };

  return query;
};

export const parseQueryToFormData = (query) => {
  return {
    // General (will be populated from job data if available)
    name: '',
    description: '',

    // Data Source
    selected_feature_extraction_job: null, // This will need to be resolved from data_source if available
    data_source: query.data_source || null,

    // Data Processing
    filter_data: Object.entries(query.filter_data || {}).map(([column, config]) => ({
      column_name: column,
      operator: config.operator,
      value: Array.isArray(config.value) ? config.value.join(', ') : config.value
    })) || [{ column_name: '', operator: '=', value: '' }],
    fill_na: query.fill_na || 0,

    // Training
    attribute: query.attribute || 'Cancer Status',
    filter_data_training: Object.entries(query.filter_data_training || {}).map(([column, config]) => ({
      column_name: column,
      operator: config.operator,
      value: Array.isArray(config.value) ? config.value.join(', ') : config.value
    })) || [{ column_name: '', operator: '=', value: '' }],
    features_to_include: {
      include_vocs: query.features_to_include?.include_vocs === 1,
      include_demo: query.features_to_include?.include_demo === 1,
      include_mirex: query.features_to_include?.include_mirex === 1
    },
    validation_technique: {
      name: query.validation_technique?.name || 'k-fold',
      k: query.validation_technique?.k || 5,
      test_size: query.validation_technique?.test_size || 0.2
    },

    // Modeling
    modeling_type: query.modeling_type || {
      name: 'classification',
      type: 'binary',
      case: ['Bladder Cancer Positive'],
      control: ['Healthy'],
      classes: []
    },
    model: query.model || 'LogisticRegression',
    normalize: query.normalize || 'RobustScaler',

    // Evaluation
    evaluation_metrics: {
      evaluation_metrics_list: query.evaluation_metrics?.evaluation_metrics_list || ['Accuracy', 'Precision', 'Recall(Sensitivity)'],
      top_n: query.evaluation_metrics?.top_n || 1
    }
  };
};
